import {useMemo, useCallback} from 'react';
import {useIntl} from 'react-intl';

import {useHistory, useNotify} from 'coreHooks';
import type TPaymentResult from 'coreClasses/payments/types/TPaymentResult';

import {usePaymentMethod} from 'commerceAPI/hooks';
import useApplePaySafeCharge from './useApplePaySafeCharge';
import useApplePayDefault from './useApplePayDefault';

/**
 * useApplePay custom hook.
 */
const useApplePay = () => {
	const history = useHistory();
	const notify = useNotify();
	const intl = useIntl();
	const paymentMethod = usePaymentMethod('DW_APPLE_PAY');
	const configuration = paymentMethod ? paymentMethod.getPublicConfigurations() : null;
	const applePayHandlerName = useMemo(() => {
		if (paymentMethod) {
			switch (paymentMethod.getPaymentProcessorID()) {
				case 'SAFECHARGE':
					return 'SAFECHARGE';
				default:
					return 'DEFAULT';
			}
		}

		return '';
	}, [paymentMethod]);
	const applePayHandlers = {
		SAFECHARGE: useApplePaySafeCharge(applePayHandlerName === 'SAFECHARGE', configuration),
		DEFAULT: useApplePayDefault(applePayHandlerName === 'DEFAULT', configuration)
	};
	const applePayHandler = applePayHandlers[applePayHandlerName] || applePayHandlers.DEFAULT;

	const handleRedirect = useCallback((url: string) => {
		const redirectUrl = new URL(url);

		history.push(`${redirectUrl.pathname}${redirectUrl.search}`);
	}, []);

	const handleError = useCallback((error: Error) => {
		if (error.message) {
			notify.error(error.message);
		} else {
			notify.error(intl.formatMessage({
				id: 'common.error.general',
				defaultMessage: 'Oops! Something went wrong.'
			}));
		}
	}, []);

	const handlePayment = useCallback((productID?: string) => {
		applePayHandler
			.handle(productID)
			.then((result: TPaymentResult) => {
				switch (result.status) {
					case 'completed':
						handleRedirect(result.redirectURL);

						break;
					case 'error':
						handleError(result.error);

						break;
					default:
						break;
				}
			});
	}, [applePayHandler]);

	return {
		loaded: applePayHandler.loaded,
		loading: applePayHandler.loading,
		handle: handlePayment
	};
};

export default useApplePay;
